import { verify } from '../auth/api.js';
import {
  clearPendingRegistration,
  getPendingRegistration,
  setAccessToken,
  setUserEmail,
} from '../auth/storage.js';
import { navigate } from '../router.js';
import { pullUserData } from '../study/userData.js';
import { errorBox, setError, setLoading } from '../components/feedback.js';
import { logoMark } from '../components/branding.js';
import { logoSvg } from '../components/icons.js';

/** Step 2 of 2 — enter the 6-digit code and exchange it for an access token. */
export function renderVerify(mount) {
  const pending = getPendingRegistration();
  if (!pending) {
    navigate('/login');
    return;
  }

  mount.className = 'min-h-dvh';
  mount.innerHTML = `
    <form id="verify-form" class="mx-auto flex w-full max-w-sm flex-col px-4 py-4" novalidate>
      <div class="flex flex-col items-center gap-4 text-center">
        ${logoSvg(64)}
        ${logoMark()}
      </div>
      
      <p class="mt-1 text-sm font-medium text-neutral-600 mt-8">
        Na podany adres e-mail wysłaliśmy kod weryfikacyjny. Podaj kod aby zalogować się do aplikacji.
      </p>

      <label for="code" class="mt-8 block text-sm font-semibold text-neutral-700 mb-1 px-1">Kod weryfikacyjny:</label>
      <input
        id="code" name="code" type="text" required inputmode="numeric" autocomplete="one-time-code"
        maxlength="6" pattern="\\d{6}"
        class="w-full rounded-xl border-2 border-neutral-300 font-medium bg-white px-4 py-3 text-base text-neutral-900 placeholder:text-neutral-400 transition focus:border-indigo-700 focus:outline-none focus:ring-1 focus:ring-indigo-700"
      />
      ${errorBox('verify-error')}

      <button
        id="verify-submit" type="submit"
        class="btn-primary mt-6 w-full px-4 py-3.5 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span class="submit-label">Zweryfikuj</span>
      </button>
      <button id="verify-back" type="button" class="mt-3 w-full text-center text-sm font-medium text-neutral-500 transition hover:text-neutral-900">
        Anuluj
      </button>
    </form>`;

  const form = mount.querySelector('#verify-form');
  const codeInput = mount.querySelector('#code');
  const errorEl = mount.querySelector('#verify-error');
  const submit = mount.querySelector('#verify-submit');
  const backButton = mount.querySelector('#verify-back');

  codeInput.focus();

  // Keep the field strictly numeric (max 6 digits).
  codeInput.addEventListener('input', () => {
    codeInput.value = codeInput.value.replace(/\D/g, '').slice(0, 6);
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    setError(errorEl, null);

    const code = codeInput.value.trim();
    if (!/^\d{6}$/.test(code)) {
      setError(errorEl, { message: 'Wpisz 6-cyfrowy kod.' });
      return;
    }

    setLoading(submit, true, 'Weryfikacja…');
    try {
      const result = await verify(pending.jwt, code);
      setAccessToken(result.jwt);
      // /verify returns the verified e-mail alongside the JWT; persist it so it
      // can be shown while signed in (falls back to the address from step 1).
      setUserEmail(result.email || pending.email);
      clearPendingRegistration();
      // Signing in is the one moment the local stores can be behind the account, so
      // this is where its backup comes back — merged into whatever this device
      // already holds (see `study/userData.js`). Best-effort and silent: the
      // account keeps its copy, the next sign-in tries again, and failing to
      // restore must never cost somebody the sign-in they just completed. Runs
      // before the redirect so home already counts the restored progress.
      try {
        await pullUserData(result.jwt);
      } catch {
        /* nothing restored — carry on into the app */
      }
      navigate('/');
    } catch (error) {
      // An expired/invalid registration token can't be recovered here — the
      // user must restart from step 1.
      const tokenExpired = error?.status === 401 && /token/i.test(error.message);
      setError(
        errorEl,
        tokenExpired ? { message: `${error.message} Rozpocznij logowanie od nowa.` } : error
      );
    } finally {
      setLoading(submit, false);
    }
  });

  backButton.addEventListener('click', () => {
    clearPendingRegistration();
    navigate('/login');
  });
}
